import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";

/**
 * Shared URL-state for calculator inputs.
 *
 * Values live in the query string so a finished calculation is shareable and
 * reproducible. Non-default values only: a clean tool URL stays clean.
 * Writes are debounced so typing doesn't spam history (replace: true anyway).
 */
const SAFE = /^[A-Za-z0-9.,-]{1,12}$/;

export function validateCalcSearch(search: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(search)) {
    if (!/^[a-z][a-zA-Z0-9]{0,20}$/.test(k)) continue;
    const s = typeof v === "string" ? v : typeof v === "number" ? String(v) : "";
    if (SAFE.test(s)) out[k] = s;
  }
  return out;
}

export function hasCalcState(search: Record<string, unknown>): boolean {
  return Object.keys(validateCalcSearch(search)).length > 0;
}

export function useCalcParam(key: string, initial: string): [string, (v: string) => void] {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const fromUrl = typeof search[key] === "string" && SAFE.test(search[key] as string)
    ? (search[key] as string)
    : undefined;
  const [value, setValue] = useState(fromUrl ?? initial);
  const navigate = useNavigate();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const set = (v: string) => {
    setValue(v);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const next = v === initial || !SAFE.test(v) ? undefined : v;
      void navigate({
        search: ((prev: Record<string, unknown>) => ({ ...prev, [key]: next })) as never,
        replace: true,
        resetScroll: false,
      });
    }, 400);
  };

  return [value, set];
}
